import { oc } from "@orpc/contract";
import { z } from "zod";
import { catalogErrors } from "./errors";
import {
  fileViewSchema,
  fileUrlsSchema,
  startedFileSchema,
  startFileInputSchema,
  urlsInputSchema,
} from "./files";
import { idSchema } from "./ids";

/**
 * The regional Files API: a person's attachments from the first byte to the last. `start` records
 * a pending file and signs an upload, the browser sends the bytes straight to the bucket, and
 * `complete` checks what arrived. The server mounts this under `/api`.
 *
 * Inputs are "detailed" so path parameters stay apart from the body. A malformed ID names
 * something that cannot exist, so the server answers an issue under `params` with NOT_FOUND and
 * anything else with VALIDATION_ERROR.
 */
const workspace = z.object({ workspaceId: z.string().min(1) });
const onFile = z.object({ params: workspace.extend({ fileId: idSchema("file") }) });
const inWorkspace = z.object({ params: workspace });

const route = oc.$route({ inputStructure: "detailed" });

export const filesContract = oc
  .errors(
    catalogErrors([
      "UNAUTHORIZED",
      "FORBIDDEN",
      "NOT_FOUND",
      "VALIDATION_ERROR",
      "INTERNAL_ERROR",
      "SERVICE_UNAVAILABLE",
    ]),
  )
  .tag("Files")
  .prefix("/workspaces/{workspaceId}/files")
  .router({
    start: route
      .route({ method: "POST", path: "/", successStatus: 201 })
      .errors(
        catalogErrors([
          "FILE_TYPE_NOT_ALLOWED",
          "FILE_TOO_LARGE",
          "FILE_LIMIT",
          "UPLOAD_RATE_LIMIT",
          "FILE_NOT_READY",
        ]),
      )
      .input(inWorkspace.extend({ body: startFileInputSchema }))
      .output(startedFileSchema),
    // Static segments are declared before `/{fileId}`; the router prefers them either way.
    urls: route
      .route({ method: "POST", path: "/urls" })
      .input(inWorkspace.extend({ body: urlsInputSchema }))
      .output(fileUrlsSchema),
    complete: route
      .route({ method: "POST", path: "/{fileId}/complete" })
      .errors(catalogErrors(["FILE_NOT_READY", "FILE_TOO_LARGE", "FILE_TYPE_NOT_ALLOWED"]))
      .input(onFile)
      .output(fileViewSchema),
    remove: route
      .route({ method: "DELETE", path: "/{fileId}" })
      .input(onFile)
      .output(z.strictObject({})),
  });

export type FilesContract = typeof filesContract;
